import { useState, useRef, useEffect } from "react";
import {
  useMobileMode,
  useOnClickOutside,
  useTranslation,
} from "Shared/util/custom-hooks";
import { Link } from "react-router-dom";
import makeComponentTrashable from "trashable-react";
import useBrandContext from "Contexts/brand-context";
import InlineSvg from "Shared/components/inline-svg";
import useUserContext from "Contexts/user-context";
import { toggleImbox } from "Shared/util/imbox-api";
import LanguageSwitch from "./language-switch";
import HeaderSearch from "./header-search";
import HeaderMenu from "./header-menu";
import Menu from "./menu";
import styles from "./header.css";

const getLogoUrl = (brand) => {
  switch (brand) {
    case "EuroFinans":
      return "/euro-finans/logo.svg";
    case "PrioritetFinans":
      return "/prioritet-finans/logo.svg";
    case "FinanceLink":
      return "/finance-link/logo.svg";
    default:
      return "";
  }
};

const Header = () => {
  const { user, loading, activeCompanyId } = useUserContext();
  const [showSearch, setShowSearch] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const topContainerRef = useRef();
  const { brandData, loadingBrandData } = useBrandContext();
  const mobileMode = useMobileMode();
  const searchRef = useRef();
  const profileMenuRef = useRef();
  useOnClickOutside(searchRef, () => setShowSearch(false));
  useOnClickOutside(profileMenuRef, () => setShowMenu(false));
  const t = useTranslation((x) => x.header);

  useEffect(() => {
    if (!loading) {
      setCustomerName(mobileMode ? "" : user.activeCompany.name);
    }
  }, [user, loading, mobileMode, activeCompanyId]);

  return (
    <div className={`noPrint mainHeader ${styles.outerContainer}`}>
      <div className={`topbarHeader ${styles.topBackground}`}>
        <div ref={topContainerRef} className={styles.topContainer}>
          {!loadingBrandData && (
            <>
              <LanguageSwitch />
              {!!brandData.imbox.widgetId ? (
                <button
                  className={styles.contactToggler}
                  onClick={toggleImbox}
                  type="button"
                >
                  <InlineSvg src="/contact.svg" />
                  <div className={styles.eventItemText}>{t.support}</div>
                </button>
              ) : (
                <div></div>
              )}
              <div ref={searchRef} className={styles.searchContainer}>
                <button
                  className={styles.searchToggler}
                  onClick={() => setShowSearch(!showSearch)}
                  type="button"
                >
                  <InlineSvg src="/search.svg" />
                  <div className={styles.eventItemText}>{t.searchInvoice}</div>
                </button>
                <HeaderSearch
                  show={showSearch}
                  hide={() => setShowSearch(false)}
                />
              </div>
              <div ref={profileMenuRef} className={styles.menuContainer}>
                <button
                  className={styles.menuToggler}
                  onClick={() => setShowMenu(!showMenu)}
                  type="button"
                >
                  <InlineSvg src="/profile.svg" />
                  <div className={styles.customerName}>{customerName}</div>
                  <div
                    className={`${styles.menuTogglerArrow} ${
                      showMenu ? styles.toggleArrowUp : styles.toggleArrowDown
                    }`}
                  >
                    <span>
                      <InlineSvg src="/caret-down.svg" />
                    </span>
                  </div>
                </button>
                <HeaderMenu
                  show={showMenu}
                  toggleShow={() => setShowMenu(!showMenu)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className={styles.bottomContainer}>
        <Link to="/">
          <InlineSvg
            src={getLogoUrl(brandData.name)}
            className={styles.logoContainer}
          />
        </Link>
        <Menu />
      </div>
    </div>
  );
};

export default makeComponentTrashable(Header);
